import md5 from "blueimp-md5";
import { cloneDeep } from "lodash";
import { useEffect, useState } from "react";

import {
  useCreatePlaybookPDFWithCustomStylingMutation,
  useGetPrintSettingsOptionsQuery,
  useUpdatePlaybookMutation,
} from "../../../redux/slices/playbook-api";
import { PrintOptions, PrintSetting } from "../../../redux/slices/playbook-api/types";
import { useEditPrintStyleMutation, useGetAllPrintStylesQuery } from "../../../redux/slices/print-style-api";
import { NewPrintStyle, PrintStyle } from "../../../redux/slices/print-style-api/types";
import { Playbook } from "../../../types/playbook/playbook";
import { sortByMostRecentlyUpdated } from "../../../utils/datetime-util";
import { DEFAULT_PRINT_SETTINGS } from "../../../utils/playbook-print-settings";

const transformPrintSettingValues = (settings: PrintSetting[]) => {
  return settings.map((setting) => {
    if (setting.value === "true") {
      return { ...setting, value: true };
    }
    if (setting.value === "false") {
      return { ...setting, value: false };
    }
    if (["1", "2", "3", "4", "5", "6"].includes(setting.value)) {
      return { ...setting, value: parseInt(setting.value, 10) };
    }
    return setting;
  });
};

const getPrintSettingsMD5 = (settings: PrintSetting[]): string => {
  const jsonString = JSON.stringify(cloneDeep(settings).sort((a, b) => a.name.localeCompare(b.name)));
  return md5(jsonString).toString();
};

type PlaybookPDFUrls = { [id: string]: string | null };

export const usePlaybookContextApi = () => {
  const [addingPlay, setAddingPlay] = useState(false);
  const [editingPrintSettings, setEditingPrintSettings] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | undefined>(undefined);
  const [localPlaybook, setLocalPlaybook] = useState<Playbook | undefined>(undefined);
  const [playsToAdd, setPlaysToAdd] = useState<string[]>([]);
  const [selectedPlaybookPlayIds, setSelectedPlaybookPlayIds] = useState<string[]>([]);
  const [updatePlaybook] = useUpdatePlaybookMutation();
  const [printSettings, setPrintSettings] = useState<PrintSetting[]>([]);
  const [printSettingsOptions, setPrintSettingsOptions] = useState<PrintOptions>();
  const [savedPrintStyle, setSavedPrintStyle] = useState<PrintStyle | undefined>(undefined);
  const { data: printOptionsData } = useGetPrintSettingsOptionsQuery();
  const [playbookError, setPlaybookError] = useState(false);
  const [playbookPDFUrls, setPlaybookPDFUrls] = useState<PlaybookPDFUrls>({});

  const { data: printStylesData, error: printStylesError, isLoading: printStylesLoading } = useGetAllPrintStylesQuery();
  const [editPrintStyle, editPrintStyleResult] = useEditPrintStyleMutation();
  const [createPlaybookPDFWithCustomStyling, { isLoading: renderingPDF }] =
    useCreatePlaybookPDFWithCustomStylingMutation();

  useEffect(() => {
    if (printOptionsData) {
      setPrintSettingsOptions(printOptionsData);
    }
  }, [printOptionsData]);

  // Use the first print style as the one and only saved/edited print style.
  useEffect(() => {
    if (printStylesData) {
      if (printStylesData.length === 1) {
        // Need to create a print style for this account.  The first one is a default that is read only.
        // In the future users will be able to make more of these as well.  For now we just need to make
        // a new one with the DEFAULT_PRINT_SETTINGS,  and then refresh the list.

        const createPrintStyle = async () => {
          const newStyle: NewPrintStyle = {
            name: "Print Settings",
            settings: DEFAULT_PRINT_SETTINGS,
          };
          await editPrintStyle(newStyle);
        };
        createPrintStyle();
      } else {
        const sortedStyles = sortByMostRecentlyUpdated([...printStylesData]);
        const mostRecent = sortedStyles[0];
        if (mostRecent) {
          setPrintSettings(mostRecent.settings);
          setSavedPrintStyle(mostRecent);
        }
      }
    }
  }, [printStylesData]);

  useEffect(() => {
    if (selectedPlaybook) {
      const playIds = selectedPlaybook.chapters
        .map((chapter) => chapter.sections.map((section) => section.plays.map((play) => play)))
        .flat(2);

      setSelectedPlaybookPlayIds(playIds);
    }
  }, [selectedPlaybook]);

  useEffect(() => {
    if (selectedPlaybook) {
      setLocalPlaybook(selectedPlaybook);
    }
  }, [selectedPlaybook]);

  useEffect(() => {
    if (localPlaybook) {
      makePdfFromPlaybook();
    }
  }, [localPlaybook]);

  // Make MD5 hash of sorted print settings.
  const printSettingsHash = printSettings.length > 0 ? getPrintSettingsMD5(printSettings) : "";

  useEffect(() => {
    if (printSettingsHash) {
      makePdfFromPlaybook(true);
    }

    // We want this effect to re-run whenever selected printSettings change.
  }, [printSettingsHash]);

  const addPlaysToPlaybook = async (playIds: string[]) => {
    if (!selectedPlaybook) return;

    const updatedPlaybook = {
      ...selectedPlaybook,
      chapters: selectedPlaybook.chapters.map((chapter) => {
        return {
          ...chapter,
          sections: chapter.sections.map((section) => {
            return {
              ...section,
              plays: [...section.plays, ...playIds],
            };
          }),
        };
      }),
    };
    await updatePlaybook(updatedPlaybook);
    makePdfFromPlaybook(true);
  };

  const removePlaysFromPlaybook = async (playIds: string[]) => {
    if (!selectedPlaybook) return;

    const updatedPlaybook = {
      ...selectedPlaybook,
      chapters: selectedPlaybook.chapters.map((chapter) => {
        return {
          ...chapter,
          sections: chapter.sections.map((section) => {
            return {
              ...section,
              plays: section.plays.filter((playId) => !playIds.includes(playId)),
            };
          }),
        };
      }),
    };
    await updatePlaybook(updatedPlaybook);
  };

  // const movePlay = (dragIndex: number, hoverIndex: number) => {
  //   if (!selectedPlaybook) return;

  //   const updatedPlaybook = {
  //     ...selectedPlaybook,
  //     chapters: selectedPlaybook.chapters.map((chapter) => {
  //       return {
  //         ...chapter,
  //         sections: chapter.sections.map((section) => {
  //           return {
  //             ...section,
  //             plays: section.plays.map((play, index) => {
  //               if (index === dragIndex) {
  //                 return section.plays[hoverIndex] || "";
  //               }
  //               if (index === hoverIndex) {
  //                 return section.plays[dragIndex] || "";
  //               }
  //               return play;
  //             }),
  //           };
  //         }),
  //       };
  //     }),
  //   };
  //   updatePlaybook(updatedPlaybook);
  // };

  const movePlayLocal = (dragIndex: number, hoverIndex: number) => {
    if (!localPlaybook) return;

    const updatedPlaybook = {
      ...localPlaybook,
      chapters: localPlaybook.chapters.map((chapter) => {
        return {
          ...chapter,
          sections: chapter.sections.map((section) => {
            return {
              ...section,
              plays: section.plays.map((play, index) => {
                if (index === dragIndex) {
                  return section.plays[hoverIndex] || "";
                }
                if (index === hoverIndex) {
                  return section.plays[dragIndex] || "";
                }
                return play;
              }),
            };
          }),
        };
      }),
    };
    setLocalPlaybook(updatedPlaybook);
  };

  const saveLocalPlaybook = async () => {
    if (!localPlaybook) return;

    await updatePlaybook(localPlaybook);
    makePdfFromPlaybook(true);
  };

  useEffect(() => {
    if (printSettings && savedPrintStyle?.id) {
      // Check if settings have changed before sending update
      const settingsChanged =
        JSON.stringify([...printSettings].sort((a, b) => a.name.localeCompare(b.name))) !==
        JSON.stringify([...savedPrintStyle.settings].sort((a, b) => a.name.localeCompare(b.name)));

      if (!settingsChanged) return;
      editPrintStyle({ ...savedPrintStyle, settings: printSettings });
    }
  }, [printSettings, editPrintStyle, savedPrintStyle]);

  const makePdfFromPlaybook = (forceRerender = false) => {
    if (!localPlaybook) return;
    const playbookId = localPlaybook.id;

    if (localPlaybook.origin === "CLASSIC") {
      setPlaybookPDFUrls((prev) => ({ ...prev, [playbookId]: localPlaybook.url }));
      return;
    }
    try {
      // Make MD5 hash of sorted print settings.
      const printSettingsHash = getPrintSettingsMD5(printSettings);

      if (!forceRerender) {
        if (localPlaybook.styleHash === printSettingsHash) {
          // If the style hash hasn't changed, we can use the existing PDF
          if (!playbookPDFUrls[playbookId])
            setPlaybookPDFUrls((prev) => ({ ...prev, [playbookId]: localPlaybook.url }));
          return;
        }
      }

      createPlaybookPDFWithCustomStyling({
        id: localPlaybook.id,
        body: { settings: transformPrintSettingValues(printSettings) },
      }).then((customPDFResponse) => {
        if ("error" in customPDFResponse) {
          console.error("Error generating PDF:", customPDFResponse.error);
          setPlaybookError(true);
          return;
        } else if (customPDFResponse.data.includes("playbookError.pdf")) {
          setPlaybookPDFUrls((prev) => ({ ...prev, [playbookId]: customPDFResponse.data }));
          setPlaybookError(true);
        } else {
          setPlaybookPDFUrls((prev) => ({ ...prev, [playbookId]: customPDFResponse.data }));
          setPlaybookError(false);
        }
      });
    } catch (err) {
      console.error(err);
    }
  };

  return {
    addingPlay,
    setAddingPlay,
    selectedPlaybook,
    setSelectedPlaybook,
    playsToAdd,
    setPlaysToAdd,
    selectedPlaybookPlayIds,
    addPlaysToPlaybook,
    removePlaysFromPlaybook,
    //movePlay,
    movePlayLocal,
    localPlaybook,
    saveLocalPlaybook,
    editingPrintSettings,
    setEditingPrintSettings,
    printSettings,
    setPrintSettings,
    printSettingsOptions,
    renderingPDF,
    playbookError,
    playbookPDFUrl: selectedPlaybook ? playbookPDFUrls[selectedPlaybook.id] || "" : "",
  };
};

export type PlaybookContextApi = ReturnType<typeof usePlaybookContextApi>;
